/**
 * Last Route Service
 *
 * Remembers the user's last visited URL so they return to it on next login.
 * Uses a dual-storage strategy:
 *   1. localStorage — instant, per-device
 *   2. Firestore subcollection `users/{uid}/preferences/navigation` — cross-device fallback
 *
 * Firestore writes are debounced (10 seconds) to minimise writes.
 * The subcollection avoids triggering user-doc Cloud Function triggers.
 */
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

const LS_KEY = 'ng_lastVisitedUrl';
const DEBOUNCE_MS = 3_000; // 3 seconds

/** Routes that should NOT be saved as "last visited" */
const EXCLUDED_PREFIXES = ['/login', '/logout', '/logging-in', '/forgot-password', '/splash', '/invitation'];

@Injectable({ providedIn: 'root' })
export class LastRouteService implements OnDestroy {
    private destroy$ = new Subject<void>();
    private firestoreWrite$ = new Subject<string>();
    private uid: string | null = null;
    private firestoreSub: Subscription | null = null;
    private tracking = false;

    constructor(
        private router: Router,
        private afs: AngularFirestore
    ) {
        // Debounce Firestore writes
        this.firestoreWrite$
            .pipe(debounceTime(DEBOUNCE_MS), takeUntil(this.destroy$))
            .subscribe((url) => this.writeToFirestore(url));
    }

    // ─── Public API ────────────────────────────────────────────

    /**
     * Start tracking navigation events for the given user.
     * Call once after successful authentication.
     */
    startTracking(uid: string): void {
        if (this.tracking && this.uid === uid) return; // Already tracking this user
        this.uid = uid;
        this.tracking = true;
        console.log(`[LastRouteService] startTracking for uid=${uid}, current URL=${this.router.url}`);

        // Clear stale route from a different user
        const stored = this.getStoredEntry();
        if (stored && stored.uid !== uid) {
            console.log(`[LastRouteService] Clearing stale URL from different user (was uid=${stored.uid})`);
            localStorage.removeItem(LS_KEY);
        }

        // Save the current URL immediately (may already be on a saveable page)
        const currentUrl = this.router.url;
        if (this.isSaveable(currentUrl)) {
            this.saveToLocalStorage(currentUrl);
            this.firestoreWrite$.next(currentUrl);
        }

        this.router.events
            .pipe(
                filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                filter((e) => this.isSaveable(e.urlAfterRedirects)),
                takeUntil(this.destroy$)
            )
            .subscribe((e) => {
                const url = e.urlAfterRedirects;
                this.saveToLocalStorage(url);
                this.firestoreWrite$.next(url);
            });
    }

    /**
     * Get the last visited URL.
     * @param callerUid — Optional uid of the user requesting the URL.
     *   Pass this from logging-in component when startTracking hasn't fired yet.
     *   If omitted, uses the uid set by startTracking().
     *
     * Strategy:
     *   1. Try localStorage (instant) — only if entry belongs to the requesting user
     *   2. Fall back to Firestore subcollection (cross-device)
     *   3. Return null if nothing found
     */
    async getLastUrl(callerUid?: string): Promise<string | null> {
        const effectiveUid = callerUid || this.uid;

        // 1. localStorage (only if it belongs to the current user)
        const stored = this.getStoredEntry();
        if (stored && stored.url && this.isSaveable(stored.url)) {
            if (effectiveUid && stored.uid === effectiveUid) {
                console.log(`[LastRouteService] Returning stored URL for current user: ${stored.url}`);
                return stored.url;
            } else if (effectiveUid && stored.uid && stored.uid !== effectiveUid) {
                // Different user — clear stale entry, fall through to Firestore
                console.log(
                    `[LastRouteService] Clearing stale URL from different user (stored uid=${stored.uid}, current uid=${effectiveUid})`
                );
                localStorage.removeItem(LS_KEY);
            } else if (!effectiveUid) {
                // No uid available yet — can't verify ownership, skip but DON'T delete
                console.log(`[LastRouteService] No uid available, skipping stored URL (preserving for later)`);
            }
        }

        // 2. Firestore
        if (effectiveUid) {
            try {
                const snap = await this.afs.doc(`users/${effectiveUid}/preferences/navigation`).ref.get();
                const data = snap.data() as { lastVisitedUrl?: string } | undefined;
                if (data?.lastVisitedUrl && this.isSaveable(data.lastVisitedUrl)) {
                    console.log(
                        `[LastRouteService] Returning Firestore URL for uid=${effectiveUid}: ${data.lastVisitedUrl}`
                    );
                    // Back-fill localStorage for next time
                    this.saveToLocalStorage(data.lastVisitedUrl);
                    return data.lastVisitedUrl;
                }
            } catch (err) {
                console.warn('[LastRouteService] Failed to read from Firestore', err);
            }
        }

        return null;
    }

    /**
     * Synchronous, side-effect-free peek at the locally-stored last URL + its uid.
     * Used by the native shell to restore the route on a cold start (the WebView
     * always boots at '/') BEFORE startTracking() overwrites localStorage with the
     * boot route. Returns null if nothing saveable is stored.
     */
    public peekStoredUrl(): { uid: string | null; url: string } | null {
        const stored = this.getStoredEntry();
        return stored && this.isSaveable(stored.url) ? stored : null;
    }

    /**
     * Stop tracking (call on logout).
     * Flushes pending URL to Firestore before stopping.
     * Keeps localStorage so the URL persists for next login.
     */
    async clear(): Promise<void> {
        console.log(`[LastRouteService] clear() called. uid=${this.uid}`);
        // Flush the current URL to Firestore before clearing uid
        const stored = this.getStoredEntry();
        if (this.uid && stored?.url) {
            console.log(`[LastRouteService] Flushing URL to Firestore before clear: ${stored.url}`);
            await this.writeToFirestore(stored.url);
        }
        this.uid = null;
        this.tracking = false;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.firestoreSub?.unsubscribe();
    }

    // ─── Private ───────────────────────────────────────────────

    private isSaveable(url: string): boolean {
        return url.startsWith('/app/') && !EXCLUDED_PREFIXES.some((prefix) => url.startsWith(prefix));
    }

    /** Save url + uid to localStorage as JSON */
    private saveToLocalStorage(url: string): void {
        const entry = JSON.stringify({ uid: this.uid, url });
        localStorage.setItem(LS_KEY, entry);
    }

    /** Read the stored entry from localStorage (handles both old string and new JSON format) */
    private getStoredEntry(): { uid: string | null; url: string } | null {
        const raw = localStorage.getItem(LS_KEY);
        if (!raw) return null;
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed.url === 'string') {
                return { uid: parsed.uid || null, url: parsed.url };
            }
        } catch {
            // Old format: plain URL string — treat as no-uid entry
            if (typeof raw === 'string' && raw.startsWith('/')) {
                return { uid: null, url: raw };
            }
        }
        return null;
    }

    private writeToFirestore(url: string): Promise<void> {
        if (!this.uid) {
            console.log('[LastRouteService] Skipped Firestore write — no uid');
            return Promise.resolve();
        }
        const path = `users/${this.uid}/preferences/navigation`;
        console.log(`[LastRouteService] Writing to Firestore: ${path} → ${url}`);
        return this.afs
            .doc(path)
            .set({ lastVisitedUrl: url, updatedOn: new Date() }, { merge: true })
            .then(() => console.log('[LastRouteService] Firestore write succeeded'))
            .catch((err) => console.error('[LastRouteService] Firestore write FAILED', err));
    }
}
